import { useCallback, useEffect, useRef, useState } from "react";

export const useIntersectionObserver = ({
	root = null,
	rootMargin = "0px",
	threshold = 0,
	freezeOnceVisible = false,
	freezeWhen,
	disabled = false,
} = {}) => {
	const nodeRef = useRef(null);
	const observerRef = useRef(null);
	const [entry, setEntry] = useState(null);
	const [frozen, setFrozen] = useState(false);

	const hasIntersectionObserver =
		typeof window !== "undefined" && "IntersectionObserver" in window;
	const off = disabled || !hasIntersectionObserver;

	const sentinelRef = useCallback(
		node => {
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
			nodeRef.current = node;

			if (!node || frozen || off) {
				return;
			}

			const rootEl = root?.current ? root.current : root ?? null;

			const obs = new IntersectionObserver(
				([e]) => {
					setEntry(e);
					const shouldFreeze =
						(freezeOnceVisible && e.isIntersecting) ||
						(typeof freezeWhen === "function" && Boolean(freezeWhen(e)));

					if (shouldFreeze) {
						setFrozen(true);
						obs.disconnect();
						observerRef.current = null;
					}
				},
				{ root: rootEl, rootMargin, threshold }
			);

			observerRef.current = obs;
			obs.observe(node);
		},
		[root, rootMargin, threshold, freezeOnceVisible, freezeWhen, frozen, off]
	);

	useEffect(() => {
		if (!nodeRef.current || off) {
			return;
		}

		sentinelRef(nodeRef.current);
	}, [
		root,
		rootMargin,
		JSON.stringify(threshold),
		freezeOnceVisible,
		freezeWhen,
		off,
		sentinelRef,
	]);

	const reset = useCallback(() => {
		setFrozen(false);
		setEntry(null);
		if (nodeRef.current && !off) {
			sentinelRef(nodeRef.current);
		}
	}, [off, sentinelRef]);

	return [sentinelRef, Boolean(entry?.isIntersecting), entry, frozen, reset];
};
